import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { AwardCeremony } from '../components/AwardRace';
import { MatchHighlightsModal } from '../components/MatchHighlightsModal';
import { MatchSimModal } from '../components/MatchSimModal';
import { CompetitionAwards } from '../components/CompetitionAwards';
import { quickSimulate, updatePlayerStats, simulateKnockoutMatch } from '../utils/simulationEngine';
import { getPlayerStat } from '../utils/gameData';
import { ArrowLeft, Play, Trophy, Lightning, SoccerBall, Eye, ArrowsLeftRight, Cards, ShieldCheck } from '@phosphor-icons/react';

// Generate cup draw: 4 groups of 5 teams
const generateCupGroups = (teams) => {
  const shuffled = [...teams].sort(() => Math.random() - 0.5);
  const groups = [];
  for (let i = 0; i < 4; i++) {
    groups.push({
      name: String.fromCharCode(65 + i),
      teamIds: shuffled.slice(i * 5, i * 5 + 5).map(t => t.id),
      cupStats: Object.fromEntries(shuffled.slice(i * 5, i * 5 + 5).map(t => [t.id, { played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, points: 0 }])),
      fixtures: [],
      completed: false
    });
  }
  groups.forEach(group => {
    const ids = group.teamIds;
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        group.fixtures.push({
          id: `${group.name}-${i}-${j}`,
          homeId: ids[i], awayId: ids[j], homeScore: null, awayScore: null, played: false, events: null, matchRatings: null
        });
      }
    }
  });
  return groups;
};

export const CupPage = ({ gameState, onUpdateGameState, modeLabel = 'EWFL' }) => {
  const navigate = useNavigate();
  const teams = gameState?.teams || [];
  const [cupState, setCupState] = useState(() => {
    const raw = gameState.cup || null;
    if (!raw) return null;
    // Migrate old format (groups with .teams array) to new format (.teamIds + .cupStats)
    if (raw.groups?.[0]?.teams && !raw.groups?.[0]?.teamIds) {
      const migrated = { ...raw, groups: raw.groups.map(g => ({
        ...g,
        teamIds: g.teams.map(t => t.id),
        cupStats: Object.fromEntries(g.teams.map(t => [t.id, t.cupStats || { played:0,won:0,drawn:0,lost:0,gf:0,ga:0,points:0 }])),
      })) };
      return migrated;
    }
    return raw;
  });
  const [showCupCeremony, setShowCupCeremony] = useState(false);
  const [highlightsMatch, setHighlightsMatch] = useState(null);
  const [simModalMatch, setSimModalMatch] = useState(null);

  const syncCup = (newCup) => {
    setCupState(newCup);
    onUpdateGameState({ ...gameState, cup: newCup });
  };

  const startCup = () => {
    const groups = generateCupGroups(teams);
    syncCup({ groups, knockout: null, phase: 'group' });
  };

  const resetCup = () => {
    if (!window.confirm('Reset the cup? All progress will be lost.')) return;
    setCupState(null);
    onUpdateGameState({ ...gameState, cup: null });
  };

  const applyMatchToCup = (cup, groupIdx, fixtureIdx, home, away, result) => {
    const next = { ...cup, groups: [...cup.groups] };
    const g = { ...next.groups[groupIdx] };
    g.fixtures = g.fixtures.map((f, i) => i === fixtureIdx
      ? { ...f, homeScore: result.homeScore, awayScore: result.awayScore, played: true, events: result.events, matchRatings: result.matchRatings }
      : f);
    const stats = { ...g.cupStats };
    stats[home.id] = { ...stats[home.id] };
    stats[away.id] = { ...stats[away.id] };
    stats[home.id].played++; stats[home.id].gf += result.homeScore; stats[home.id].ga += result.awayScore;
    stats[away.id].played++; stats[away.id].gf += result.awayScore; stats[away.id].ga += result.homeScore;
    const isHomeWin = result.homeScore > result.awayScore;
    const isDraw = result.homeScore === result.awayScore;
    if (isHomeWin) { stats[home.id].won++; stats[home.id].points += 3; stats[away.id].lost++; }
    else if (isDraw) { stats[home.id].drawn++; stats[home.id].points++; stats[away.id].drawn++; stats[away.id].points++; }
    else { stats[away.id].won++; stats[away.id].points += 3; stats[home.id].lost++; }
    g.cupStats = stats;
    g.completed = g.fixtures.every(f => f.played);
    next.groups[groupIdx] = g;
    return next;
  };

  const simOneGroupMatch = (groupIdx, fixtureIdx) => {
    const g = cupState.groups[groupIdx];
    const f = g.fixtures[fixtureIdx];
    if (f.played) return;
    const home = teams.find(t => t.id === f.homeId);
    const away = teams.find(t => t.id === f.awayId);
    if (!home || !away) return;
    const result = quickSimulate(home, away);
    const newCup = applyMatchToCup(cupState, groupIdx, fixtureIdx, home, away, result);
    // Update player stats for cup
    const updatedTeams = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cup');
    setCupState(newCup);
    onUpdateGameState({ ...gameState, teams: updatedTeams, cup: newCup });
  };

  const simAllGroups = () => {
    let currentCup = cupState;
    let currentTeams = teams;
    currentCup.groups.forEach((group, gi) => {
      group.fixtures.forEach((f, fi) => {
        if (f.played) return;
        const home = currentTeams.find(t => t.id === f.homeId);
        const away = currentTeams.find(t => t.id === f.awayId);
        if (!home || !away) return;
        const result = quickSimulate(home, away);
        currentCup = applyMatchToCup(currentCup, gi, fi, home, away, result);
        currentTeams = updatePlayerStats(currentTeams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cup');
      });
    });
    setCupState(currentCup);
    onUpdateGameState({ ...gameState, teams: currentTeams, cup: currentCup });
  };

  const getGroupStandings = (group) => {
    return [...group.teamIds].map(id => ({ id, stats: group.cupStats[id] || { played:0,won:0,drawn:0,lost:0,gf:0,ga:0,points:0 } }))
      .sort((a, b) => {
        if (b.stats.points !== a.stats.points) return b.stats.points - a.stats.points;
        const gdA = a.stats.gf - a.stats.ga; const gdB = b.stats.gf - b.stats.ga;
        if (gdB !== gdA) return gdB - gdA;
        return b.stats.gf - a.stats.gf;
      });
  };

  const advanceToKnockout = () => {
    const qualifiers = [];
    cupState.groups.forEach(group => {
      const standings = getGroupStandings(group);
      qualifiers.push(standings[0].id, standings[1].id);
    });
    const shuffled = qualifiers.sort(() => Math.random() - 0.5);
    const qf = [];
    for (let i = 0; i < 4; i++) {
      qf.push({ homeId: shuffled[i * 2], awayId: shuffled[i * 2 + 1], leg1: null, leg2: null, winnerId: null, leg1Events: null, leg2Events: null });
    }
    syncCup({ ...cupState, knockout: { qf, sf: [], final: null }, phase: 'qf' });
  };

  const simKnockoutMatch = (roundKey, tieIdx, leg) => {
    const ko = cupState.knockout;
    const tie = ko[roundKey][tieIdx];
    if (tie.winnerId) return;
    const homeId = leg === 1 ? tie.homeId : tie.awayId;
    const awayId = leg === 1 ? tie.awayId : tie.homeId;
    const home = teams.find(t => t.id === homeId);
    const away = teams.find(t => t.id === awayId);
    if (!home || !away) return;
    // For leg 2 of a 2-legged tie, include leg 1 as aggregate
    const aggregateHomeExtra = (leg === 2 && tie.leg1) ? (tie.leg1.away || 0) : 0;
    const aggregateAwayExtra = (leg === 2 && tie.leg1) ? (tie.leg1.home || 0) : 0;
    const isFinal = roundKey === 'final';
    const needsKnockoutLogic = isFinal || (leg === 2);
    const result = needsKnockoutLogic
      ? simulateKnockoutMatch(home, away, aggregateHomeExtra, aggregateAwayExtra)
      : quickSimulate(home, away);
    const newTie = { ...tie };
    if (roundKey === 'final') {
      newTie.leg1 = { home: result.homeScore, away: result.awayScore };
      newTie.leg1Events = result.events;
      newTie.leg1Ratings = result.matchRatings;
      newTie.leg1Pens = result.pens || null;
      newTie.winnerId = result.winner === 'home' ? homeId : result.winner === 'away' ? awayId : (result.homeScore >= result.awayScore ? homeId : awayId);
    } else {
      if (leg === 1) {
        newTie.leg1 = { home: result.homeScore, away: result.awayScore };
        newTie.leg1Events = result.events;
        newTie.leg1Ratings = result.matchRatings;
      } else {
        newTie.leg2 = { home: result.homeScore, away: result.awayScore };
        newTie.leg2Events = result.events;
        newTie.leg2Ratings = result.matchRatings;
        newTie.leg2Pens = result.pens || null;
        // Winner logic: if pens, use that. Else aggregate (leg1 & leg2).
        if (result.pens) {
          newTie.winnerId = result.pens.winner === 'home' ? tie.awayId : tie.homeId; // leg2 home is tie.away
        } else {
          const homeAgg = (newTie.leg1?.home || 0) + (newTie.leg2?.away || 0);
          const awayAgg = (newTie.leg1?.away || 0) + (newTie.leg2?.home || 0);
          newTie.winnerId = homeAgg > awayAgg ? tie.homeId : awayAgg > homeAgg ? tie.awayId : tie.homeId;
        }
      }
    }
    const newKO = { ...ko, [roundKey]: ko[roundKey].map((t, i) => i === tieIdx ? newTie : t) };
    const updatedTeams = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cup');
    if (roundKey === 'qf' && newKO.qf.every(t => t.winnerId)) {
      const sf = [];
      for (let i = 0; i < 2; i++) sf.push({ homeId: newKO.qf[i * 2].winnerId, awayId: newKO.qf[i * 2 + 1].winnerId, leg1: null, leg2: null, winnerId: null });
      newKO.sf = sf;
    } else if (roundKey === 'sf' && newKO.sf.every(t => t.winnerId)) {
      newKO.final = [{ homeId: newKO.sf[0].winnerId, awayId: newKO.sf[1].winnerId, leg1: null, leg2: null, winnerId: null }];
    }
    const phase = newKO.final?.[0]?.winnerId ? 'complete' : (newKO.final ? 'final' : newKO.sf.length ? 'sf' : cupState.phase);
    const newCup = { ...cupState, knockout: newKO, phase };
    // Record trophy
    if (phase === 'complete' && newKO.final?.[0]?.winnerId) {
      const champion = teams.find(t => t.id === newKO.final[0].winnerId);
      const history = gameState.trophyHistory || [];
      const alreadyRecorded = history.some(h => h.season === gameState.season && h.competition === 'cup');
      if (!alreadyRecorded && champion) {
        history.push({ season: gameState.season, year: gameState.year || 2026, competition: 'cup', competitionLabel: 'Cup', winnerId: champion.id, winnerName: champion.name });
      }
      setCupState(newCup);
      onUpdateGameState({ ...gameState, teams: updatedTeams, cup: newCup, trophyHistory: history });
      return;
    }
    setCupState(newCup);
    onUpdateGameState({ ...gameState, teams: updatedTeams, cup: newCup });
  };

  const openFullSim = (home, away, onApply) => {
    setSimModalMatch({ home, away, onApply });
  };

  // Open group match in full-sim modal
  const openGroupMatchSim = (groupIdx, fixtureIdx) => {
    const g = cupState.groups[groupIdx];
    const f = g.fixtures[fixtureIdx];
    const home = teams.find(t => t.id === f.homeId);
    const away = teams.find(t => t.id === f.awayId);
    if (!home || !away || f.played) return;
    openFullSim(home, away, (result) => {
      const newCup = applyMatchToCup(cupState, groupIdx, fixtureIdx, home, away, result);
      const updatedTeams = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cup');
      setCupState(newCup);
      onUpdateGameState({ ...gameState, teams: updatedTeams, cup: newCup });
    });
  };

  const allGroupsDone = cupState?.groups?.every(g => g.completed);
  const getTeam = (id) => teams.find(t => t.id === id);

  // Cup top scorers
  const cupStats = useMemo(() => {
    const players = teams.flatMap(t => t.players.map(p => ({ ...p, teamName: t.name })));
    return {
      scorers: [...players].filter(p => getPlayerStat(p, 'goals', 'cup') > 0).sort((a, b) => getPlayerStat(b, 'goals', 'cup') - getPlayerStat(a, 'goals', 'cup')).slice(0, 100),
      assists: [...players].filter(p => getPlayerStat(p, 'assists', 'cup') > 0).sort((a, b) => getPlayerStat(b, 'assists', 'cup') - getPlayerStat(a, 'assists', 'cup')).slice(0, 100),
      cards: [...players].filter(p => getPlayerStat(p, 'yellowCards', 'cup') > 0).sort((a, b) => getPlayerStat(b, 'yellowCards', 'cup') - getPlayerStat(a, 'yellowCards', 'cup')).slice(0, 100),
      cs: [...players].filter(p => getPlayerStat(p, 'cleanSheets', 'cup') > 0).sort((a, b) => getPlayerStat(b, 'cleanSheets', 'cup') - getPlayerStat(a, 'cleanSheets', 'cup')).slice(0, 100)
    };
  }, [teams]);

  const KnockoutTie = ({ tie, idx, roundKey, single = false }) => {
    const home = getTeam(tie.homeId);
    const away = getTeam(tie.awayId);
    if (!home || !away) return null;
    return (
      <div className="card-elevated rounded-sm p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className={`text-sm flex-1 truncate ${tie.winnerId === tie.homeId ? 'text-[#32D74B] font-bold' : 'text-white'}`}>{home.name}</span>
          {tie.leg1 && <span className="font-mono text-sm text-white">{tie.leg1.home}-{tie.leg1.away}</span>}
          {tie.leg2 && <span className="font-mono text-sm text-[#A0A0A5]">/ {tie.leg2.home}-{tie.leg2.away}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm flex-1 truncate ${tie.winnerId === tie.awayId ? 'text-[#32D74B] font-bold' : 'text-white'}`}>{away.name}</span>
        </div>
        {tie.winnerId && <p className="text-xs text-[#32D74B]">Winner: {getTeam(tie.winnerId)?.name}</p>}
        <div className="flex gap-1 flex-wrap">
          {!tie.leg1 && (
            <>
              <Button size="sm" onClick={() => simKnockoutMatch(roundKey, idx, 1)} className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white text-xs h-7" data-testid={`sim-${roundKey}-${idx}-leg1`}>
                <Lightning size={12} className="mr-1" /> Leg 1 Quick
              </Button>
              <Button size="sm" onClick={() => setSimModalMatch({
                home, away,
                knockout: roundKey === 'final',
                aggregateHomeExtra: 0, aggregateAwayExtra: 0,
                onApply: (result) => simKnockoutApplyResult(roundKey, idx, 1, home, away, result)
              })} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white text-xs h-7" data-testid={`full-${roundKey}-${idx}-leg1`}>
                <Play size={12} className="mr-1" /> Leg 1 Full
              </Button>
            </>
          )}
          {tie.leg1 && !single && !tie.leg2 && (
            <>
              <Button size="sm" onClick={() => simKnockoutMatch(roundKey, idx, 2)} className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white text-xs h-7" data-testid={`sim-${roundKey}-${idx}-leg2`}>
                <Lightning size={12} className="mr-1" /> Leg 2 Quick
              </Button>
              <Button size="sm" onClick={() => setSimModalMatch({
                home: away, away: home,
                knockout: true,
                aggregateHomeExtra: tie.leg1.away || 0,
                aggregateAwayExtra: tie.leg1.home || 0,
                onApply: (result) => simKnockoutApplyResult(roundKey, idx, 2, away, home, result)
              })} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white text-xs h-7" data-testid={`full-${roundKey}-${idx}-leg2`}>
                <Play size={12} className="mr-1" /> Leg 2 Full
              </Button>
            </>
          )}
          {tie.leg1 && (
            <Button size="sm" variant="outline" onClick={() => setHighlightsMatch({
              match: { homeTeamName: home.name, awayTeamName: away.name, homeScore: tie.leg1.home, awayScore: tie.leg1.away, events: tie.leg1Events, matchRatings: tie.leg1Ratings },
              homeTeam: home, awayTeam: away
            })} className="text-xs h-7 bg-transparent border-[#2C2C35] text-white" data-testid={`hl-${roundKey}-${idx}-leg1`}>
              <Eye size={12} className="mr-1" /> Leg 1 Stats
            </Button>
          )}
          {tie.leg2 && (
            <Button size="sm" variant="outline" onClick={() => setHighlightsMatch({
              match: { homeTeamName: away.name, awayTeamName: home.name, homeScore: tie.leg2.home, awayScore: tie.leg2.away, events: tie.leg2Events, matchRatings: tie.leg2Ratings },
              homeTeam: away, awayTeam: home
            })} className="text-xs h-7 bg-transparent border-[#2C2C35] text-white" data-testid={`hl-${roundKey}-${idx}-leg2`}>
              <Eye size={12} className="mr-1" /> Leg 2 Stats
            </Button>
          )}
        </div>
      </div>
    );
  };

  const simKnockoutApplyResult = (roundKey, tieIdx, leg, home, away, result) => {
    const ko = cupState.knockout;
    const tie = { ...ko[roundKey][tieIdx] };
    if (leg === 1) { tie.leg1 = { home: result.homeScore, away: result.awayScore }; tie.leg1Events = result.events; tie.leg1Ratings = result.matchRatings; tie.leg1Pens = result.pens || null; }
    else { tie.leg2 = { home: result.homeScore, away: result.awayScore }; tie.leg2Events = result.events; tie.leg2Ratings = result.matchRatings; tie.leg2Pens = result.pens || null;
      if (result.pens) {
        tie.winnerId = result.pens.winner === 'home' ? tie.awayId : tie.homeId; // leg2 home is original away
      } else {
        const homeAgg = (tie.leg1?.home || 0) + (tie.leg2?.away || 0);
        const awayAgg = (tie.leg1?.away || 0) + (tie.leg2?.home || 0);
        tie.winnerId = homeAgg > awayAgg ? tie.homeId : awayAgg > homeAgg ? tie.awayId : tie.homeId;
      }
    }
    if (roundKey === 'final') {
      tie.leg1Pens = result.pens || null;
      tie.winnerId = result.pens ? (result.pens.winner === 'home' ? tie.homeId : tie.awayId) : (result.homeScore >= result.awayScore ? tie.homeId : tie.awayId);
    }
    const newKO = { ...ko, [roundKey]: ko[roundKey].map((t, i) => i === tieIdx ? tie : t) };
    if (roundKey === 'qf' && newKO.qf.every(t => t.winnerId)) {
      newKO.sf = [
        { homeId: newKO.qf[0].winnerId, awayId: newKO.qf[1].winnerId, leg1: null, leg2: null, winnerId: null },
        { homeId: newKO.qf[2].winnerId, awayId: newKO.qf[3].winnerId, leg1: null, leg2: null, winnerId: null }
      ];
    } else if (roundKey === 'sf' && newKO.sf.every(t => t.winnerId)) {
      newKO.final = [{ homeId: newKO.sf[0].winnerId, awayId: newKO.sf[1].winnerId, leg1: null, leg2: null, winnerId: null }];
    }
    const phase = newKO.final?.[0]?.winnerId ? 'complete' : (newKO.final ? 'final' : newKO.sf.length ? 'sf' : cupState.phase);
    const newCup = { ...cupState, knockout: newKO, phase };
    const updatedTeams = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cup');
    let trophyHistory = gameState.trophyHistory || [];
    if (phase === 'complete' && newKO.final?.[0]?.winnerId) {
      const champion = teams.find(t => t.id === newKO.final[0].winnerId);
      const alreadyRecorded = trophyHistory.some(h => h.season === gameState.season && h.competition === 'cup');
      if (!alreadyRecorded && champion) {
        trophyHistory = [...trophyHistory, { season: gameState.season, year: gameState.year || 2026, competition: 'cup', competitionLabel: 'Cup', winnerId: champion.id, winnerName: champion.name }];
      }
    }
    setCupState(newCup);
    onUpdateGameState({ ...gameState, teams: updatedTeams, cup: newCup, trophyHistory });
  };

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white" data-testid="cup-back-btn"><ArrowLeft size={20} /></Button>
          <div className="flex-1">
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide">{modeLabel} Cup</h1>
            <p className="text-[#A0A0A5] mt-1">4 groups of 5 • Top 2 advance • Knockout to Final</p>
          </div>
          {cupState && (
            <Button variant="outline" onClick={resetCup} className="bg-transparent border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white" data-testid="reset-cup-btn">
              Reset Cup
            </Button>
          )}
        </div>

        {!cupState ? (
          <div className="card-surface rounded-sm p-12 text-center">
            <Trophy size={64} weight="fill" className="text-[#FFD700] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">Start Cup</h2>
            <p className="text-[#A0A0A5] mb-6">{teams.length} teams drawn into 4 groups. Top 2 from each group advance to QF.</p>
            <Button data-testid="start-cup-btn" onClick={startCup} className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold">
              <Play size={18} className="mr-2" weight="fill" /> Draw Groups & Start
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="bracket" className="w-full">
            <TabsList className="bg-[#121216] border border-[#2C2C35] w-full grid grid-cols-2 mb-6">
              <TabsTrigger value="bracket" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="cup-tab-bracket">Bracket</TabsTrigger>
              <TabsTrigger value="awards" className="data-[state=active]:bg-[#FF9500] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="cup-tab-awards">Awards</TabsTrigger>
            </TabsList>

            <TabsContent value="bracket">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading font-bold text-xl text-white uppercase">Group Stage</h2>
                  <div className="flex gap-2">
                    {false && !allGroupsDone && (
                      null
                    )}
                    {allGroupsDone && !cupState.knockout && (
                      <Button data-testid="advance-knockout-btn" onClick={advanceToKnockout} className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold" size="sm">
                        <Trophy size={16} className="mr-1" /> Advance to Knockouts
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {cupState.groups.map((group, gi) => {
                    const standings = getGroupStandings(group);
                    return (
                      <div key={gi} className="card-surface rounded-sm" data-testid={`group-${group.name}`}>
                        <div className="border-b border-[#2C2C35] px-4 py-2">
                          <h3 className="font-heading font-bold text-white">Group {group.name}</h3>
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent border-b border-[#2C2C35]">
                              <TableHead className="text-[#6E6E73] font-mono text-xs">Team</TableHead>
                              <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-8">P</TableHead>
                              <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-8">W</TableHead>
                              <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-8">D</TableHead>
                              <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-8">L</TableHead>
                              <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">GD</TableHead>
                              <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">Pts</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {standings.map((row, idx) => {
                              const team = getTeam(row.id);
                              const gd = row.stats.gf - row.stats.ga;
                              return (
                                <TableRow key={row.id} className={`border-b border-[#2C2C35] ${idx < 2 ? 'border-l-2 border-l-[#32D74B]' : ''} ${idx % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`}>
                                  <TableCell className="text-sm text-white truncate max-w-[120px]">{team?.name}</TableCell>
                                  <TableCell className="font-mono text-xs text-center text-[#A0A0A5]">{row.stats.played}</TableCell>
                                  <TableCell className="font-mono text-xs text-center text-[#32D74B]">{row.stats.won}</TableCell>
                                  <TableCell className="font-mono text-xs text-center text-[#A0A0A5]">{row.stats.drawn}</TableCell>
                                  <TableCell className="font-mono text-xs text-center text-[#FF3B30]">{row.stats.lost}</TableCell>
                                  <TableCell className={`font-mono text-xs text-center ${gd > 0 ? 'text-[#32D74B]' : gd < 0 ? 'text-[#FF3B30]' : 'text-[#A0A0A5]'}`}>{gd > 0 ? `+${gd}` : gd}</TableCell>
                                  <TableCell className="font-mono text-xs text-center font-bold text-white">{row.stats.points}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        {/* Group fixtures */}
                        <div className="p-2 space-y-1 border-t border-[#2C2C35]">
                          <p className="text-[10px] font-bold uppercase text-[#6E6E73] px-1">Matches</p>
                          {group.fixtures.map((f, fi) => {
                            const home = getTeam(f.homeId);
                            const away = getTeam(f.awayId);
                            return (
                              <div key={f.id} className="flex items-center gap-2 text-xs p-1.5 bg-[#0A0A0C] rounded-sm" data-testid={`group-${group.name}-fixture-${fi}`}>
                                <span className="text-white flex-1 truncate text-right">{home?.name}</span>
                                {f.played ? <span className="font-mono font-bold text-white px-2">{f.homeScore}-{f.awayScore}</span> : <span className="text-[#6E6E73] px-2">vs</span>}
                                <span className="text-white flex-1 truncate">{away?.name}</span>
                                {!f.played ? (
                                  <div className="flex gap-1">
                                    <Button size="sm" onClick={() => simOneGroupMatch(gi, fi)} className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white h-6 text-[10px] px-2" data-testid={`sim-g${group.name}-${fi}`}>Quick</Button>
                                    <Button size="sm" onClick={() => openGroupMatchSim(gi, fi)} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white h-6 text-[10px] px-2" data-testid={`full-g${group.name}-${fi}`}>Full</Button>
                                  </div>
                                ) : (
                                  <Button size="sm" variant="outline" onClick={() => setHighlightsMatch({ match: { ...f, homeTeamName: home.name, awayTeamName: away.name }, homeTeam: home, awayTeam: away })} className="h-6 text-[10px] px-2 bg-transparent border-[#2C2C35] text-white" data-testid={`hl-g${group.name}-${fi}`}>
                                    <Eye size={10} className="mr-1" /> Stats
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {cupState.knockout && (
                  <div className="space-y-6 mt-8">
                    {['qf', 'sf', 'final'].map(roundKey => {
                      const round = cupState.knockout[roundKey];
                      if (!round || round.length === 0) return null;
                      const roundLabel = roundKey === 'qf' ? 'Quarter-Finals (H&A)' : roundKey === 'sf' ? 'Semi-Finals (H&A)' : 'Final';
                      return (
                        <div key={roundKey}>
                          <h2 className="font-heading font-bold text-xl text-white uppercase mb-3">{roundLabel}</h2>
                          <div className={`grid gap-4 ${roundKey === 'final' ? 'grid-cols-1 max-w-md mx-auto' : 'grid-cols-1 sm:grid-cols-2'}`}>
                            {round.map((tie, idx) => <KnockoutTie key={idx} tie={tie} idx={idx} roundKey={roundKey} single={roundKey === 'final'} />)}
                          </div>
                        </div>
                      );
                    })}
                    {cupState.phase === 'complete' && cupState.knockout.final?.[0]?.winnerId && (
                      <div className="card-surface rounded-sm p-8 text-center">
                        <Trophy size={64} weight="fill" className="text-[#FFD700] mx-auto mb-3" />
                        <h2 className="font-heading font-black text-3xl text-[#FFD700] uppercase">Cup Winners!</h2>
                        <p className="text-white text-xl mt-2">{getTeam(cupState.knockout.final[0].winnerId)?.name}</p>
                        <Button onClick={() => setShowCupCeremony(true)} className="mt-4 bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold" data-testid="view-cup-awards-btn">
                          <Trophy size={18} className="mr-2" weight="fill" /> View Cup Awards
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="awards">
              <CompetitionAwards teams={teams} competition="cup" topN={25} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Dialog open={showCupCeremony} onOpenChange={setShowCupCeremony}>
        <DialogContent className="bg-[#121216] border-[#2C2C35] max-w-2xl" data-testid="cup-award-ceremony">
          <AwardCeremony teams={teams} type="cup" label="Cup" />
        </DialogContent>
      </Dialog>

      <MatchHighlightsModal
        isOpen={!!highlightsMatch}
        onClose={() => setHighlightsMatch(null)}
        match={highlightsMatch?.match}
        homeTeam={highlightsMatch?.homeTeam}
        awayTeam={highlightsMatch?.awayTeam}
        competitionLabel={`${modeLabel} Cup`}
      />

      <MatchSimModal
        isOpen={!!simModalMatch}
        onClose={() => setSimModalMatch(null)}
        homeTeam={simModalMatch?.home}
        awayTeam={simModalMatch?.away}
        competitionLabel={`${modeLabel} Cup`}
        knockout={simModalMatch?.knockout || false}
        aggregateHomeExtra={simModalMatch?.aggregateHomeExtra || 0}
        aggregateAwayExtra={simModalMatch?.aggregateAwayExtra || 0}
        onResult={(result) => { simModalMatch?.onApply?.(result); }}
      />
    </div>
  );
};

const CupScorerList = ({ label, data, statKey, icon: Icon, color, comp }) => (
  <div className="card-surface rounded-sm">
    <div className="border-b border-[#2C2C35] px-4 py-3 flex items-center gap-2">
      <Icon size={18} className={color} weight="fill" />
      <h3 className="font-heading font-bold text-white uppercase tracking-wide">Top 100 {label}</h3>
    </div>
    <ScrollArea className="h-[500px]">
      {data.length === 0 ? (
        <div className="p-6 text-center text-[#6E6E73]"><p>No cup stats yet. Play matches.</p></div>
      ) : (
        <div className="p-2">
          {data.map((p, i) => (
            <div key={p.id} className={`flex items-center gap-2 px-2 py-1.5 ${i % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`}>
              <span className={`font-mono text-sm font-bold w-6 ${i === 0 ? 'text-[#FFD700]' : 'text-[#A0A0A5]'}`}>{i + 1}</span>
              <span className="nationality-flag text-sm">{p.flag}</span>
              <span className="text-sm text-white flex-1 truncate">{p.name}</span>
              <span className="text-xs text-[#6E6E73] truncate max-w-[100px]">{p.teamName}</span>
              <span className={`font-mono text-sm font-bold ${color}`}>{getPlayerStat(p, statKey, comp)}</span>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  </div>
);
